import React, { ReactElement } from 'react';
import { NextPageContext } from 'next';
import { useIntl } from 'react-intl';
import SubmitJobPostingsPageLayout
  from '@fuww/library/src/Layouts/SubmitJobPostingsPage';
import Button from '@fuww/library/src/Button';
import correctLocalizedRoute from '../lib/correctLocalizedRoute';
import messages from '../lib/messages.json';
import Head from '../components/Head';
import Layout from '../components/Layout';
import Hero from '../components/Hero';
import LinkAndAnchor from '../components/LinkAndAnchor';
import createCompanyBreadcrumbs from '../lib/createCompanyBreadcrumbs';
import { useSiteContext } from '../components/SiteContext';

const headerImageUrls = {
  mobile: [
    'https://r.fashionunited.com/dQdpLvHZKn-q3uPvUn7ZRyd1cePPBXE3fmeaiNuwT2w/resize:fill-down:42:34:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvc3VibWl0LXlvdXItam9iLXBvc3RpbmdzLW1vYmlsZS5wbmc',
    'https://r.fashionunited.com/wygjdRu5PwNgikVd7ePgg-piqtmGUPM5TOVJm3cgPRs/resize:fill-down:360:288:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvc3VibWl0LXlvdXItam9iLXBvc3RpbmdzLW1vYmlsZS5wbmc',
    'https://r.fashionunited.com/pjTFlfFflDo3bMrsE4z1-8r0en8IGdeBuZxwC9AKe4s/resize:fill-down:720:576:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvc3VibWl0LXlvdXItam9iLXBvc3RpbmdzLW1vYmlsZS5wbmc',
  ],
  desktop: [
    'https://r.fashionunited.com/8h0mkhZFYjDXgpDWAhYxMY66SEkmF3KQr9w4aMEEAq0/resize:fill-down:42:8:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvc3VibWl0LXlvdXItam9iLXBvc3RpbmdzLW1vYmlsZS5wbmc',
    'https://r.fashionunited.com/838D8p6kUjqhy9O5F3PgL-uogxGyDkhJYj9TdoOr_a4/resize:fill-down:1350:277:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvc3VibWl0LXlvdXItam9iLXBvc3RpbmdzLW1vYmlsZS5wbmc',
    'https://r.fashionunited.com/B1xkuapgig4d6BT1zhHMqiBK_4pSJ1PYr7Qu7-pS-Dk/resize:fill-down:2560:526:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvc3VibWl0LXlvdXItam9iLXBvc3RpbmdzLW1vYmlsZS5wbmc',
    'https://r.fashionunited.com/BccfEUn3tEpKVM-ThgMvPfzmT3tuiMuK5eGOCF2OUpw/resize:fill-down:5120:1052:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvc3VibWl0LXlvdXItam9iLXBvc3RpbmdzLW1vYmlsZS5wbmc',
  ],
};

const SubmitJobPostingsPage = () => {
  const intl = useIntl();
  const { locale } = useSiteContext();

  const breadcrumbs = createCompanyBreadcrumbs({
    intl,
    crumbRoute: `submit-job-postings.${locale}`,
    crumbLabel: intl.formatMessage(messages['submitJobPostings.header']),
  });

  return (
    <>
      <Head
        title={intl.formatMessage(messages['submitJobPostings.header'])}
        description=""
      />
      <SubmitJobPostingsPageLayout
        bodyText={(
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(
                messages['submitJobPostings.bodyText'],
              ),
            }}
          />
        )}
        breadcrumbs={breadcrumbs}
        button={(
          <LinkAndAnchor
            href="https://dashboard.fashionunited.com/jobs/new"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="contained">
              {intl.formatMessage(messages['submitJobPostings.buttonLabel'])}
            </Button>
          </LinkAndAnchor>
        )}
        callToActionText={(
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage(
                messages['submitJobPostings.callToActionText'],
              ),
            }}
          />
        )}
        description={intl.formatMessage(
          messages['submitJobPostings.description'],
        )}
        title={intl.formatMessage(messages['submitJobPostings.header'])}
      />
    </>
  );
};

SubmitJobPostingsPage.getLayout = (page: ReactElement) => (
  <Layout
    header={(
      <Hero
        alt="Submit job postings banner image"
        imageUrls={headerImageUrls}
      />
    )}
  >
    {page}
  </Layout>
);

SubmitJobPostingsPage.getInitialProps = (context: NextPageContext) => {
  correctLocalizedRoute(context, 'submit-job-postings');
  return {};
};

export default SubmitJobPostingsPage;
